import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { arraysEqual, parseToDate, _ } from '~app/shared/utils';
import { combineLatest, Subject, Observable } from 'rxjs';
import { LoadingConfig } from '~app/components/qm-directives/loading.directive';
import { TagState } from './measurements/neve-measurements.interface';
import { NeveMeasurementsService } from './measurements/neve-measurements.service';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { isEmptyValmetDnaConfiguredTag, OptionalValmetDnaConfiguredTag, TagPair, ValmetDnaConfiguredTag, ValmetDnaTransformedValue }
  from '~app/shared/map/layer/layer-definitions/T14';
import { differenceInMinutes } from 'date-fns';
import { NgbdSortableHeaderDirective, SortEvent } from '~app/components/qm-directives/sortable-header.directive';
import * as lodash from 'lodash';
import { createTableDataUngrouped, getTableRowUngrouped, TableDataUngrouped } from './neve-measurements-table-ungrouped';
import { createGroupedUnsortedRows, createTableDataGrouped, GroupedColumnConfig, TableDataGrouped } from './neve-measurements-table-grouped';

export type TagpairState = {
  tagpair: TagPair,
  state: TagState & {
    transformedMeasurements?: ValmetDnaTransformedValue[];
  },
  config: ValmetDnaConfiguredTag
};

type ConfigUngrouped = {
  tableType: 'ungrouped',
  valmetDnaTags: ValmetDnaConfiguredTag[]
};

type ConfigGrouped = {
  tableType: 'grouped',
  /**
   * Currently required until grouping uses linking
   */
  groupNames: string[],
  columns: GroupedColumnConfig[]
};

@Component({
  selector: 'qm-neve-measurements-table',
  templateUrl: './neve-measurements-table.component.html',
  styleUrls: ['./neve-measurements-table.component.scss']
})
export class NeveMeasurementsTableComponent implements OnInit, OnDestroy {
  public tableData: {
    ungrouped$?: Observable<TableDataUngrouped>;
    grouped$?: Observable<TableDataGrouped>;
  } = {};
  public loading: boolean;
  public loadingConfig: LoadingConfig = {
    timeout: 0,
    messages: {
      timeout: _('Please wait a moment, fetching data from Valmet DNA'),
      empty: _('Measurement data is currently not available'),
    }
  };

  @Input('config')
  public config: ConfigUngrouped | ConfigGrouped;

  @ViewChildren(NgbdSortableHeaderDirective) headers: QueryList<NgbdSortableHeaderDirective>;

  private readonly ngUnsubscribe = new Subject<void>();

  private tagConfigs: OptionalValmetDnaConfiguredTag[];
  private nonEmptyTagConfigs: ValmetDnaConfiguredTag[];
  private tagpairs: TagPair[];
  private readonly sortEvents = new Subject<SortEvent>();
  private readonly sortEvents$: Observable<SortEvent>;

  constructor(private readonly measurements: NeveMeasurementsService) {
    this.sortEvents$ = this.sortEvents.asObservable().pipe(
      startWith({
        column: '',
        direction: ''
      })
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onSort(event: SortEvent) {
    this.sortEvents.next(event);
  }

  async ngOnInit() {
    this.tagConfigs = this.config.tableType === 'grouped' ?
      this.config.columns.flatMap(col => col.valmetDnaTags) : this.config.valmetDnaTags;
    this.nonEmptyTagConfigs = this.tagConfigs.filter(config => {
      if (isEmptyValmetDnaConfiguredTag(config)) {
        return false;
      }
      return true;
    }) as ValmetDnaConfiguredTag[];
    this.tagpairs = this.nonEmptyTagConfigs.map(config => config.tagpair);

    this.loading = true;
    // for now only starts querying once component inits
    this.measurements.init(this.tagpairs);
    const allTagStates = this.tagpairs.map(tagpair =>
      this.measurements.tagState$(tagpair)
        .pipe(
          map(state => {
            const config = this.nonEmptyTagConfigs.find(nonEmptyConfig => arraysEqual(nonEmptyConfig.tagpair, tagpair));
            return {
              tagpair,
              state: {
                metadata: state.metadata,
                status: state.status,
                transformedMeasurements: state.measurements?.map(m => config.valueTransformer(m))
              },
              config
            } as TagpairState;
          }),
          takeUntil(this.ngUnsubscribe)
        )
    );
    const latestTagStates$ = combineLatest(allTagStates);

    latestTagStates$.pipe(
      take(1),
      takeUntil(this.ngUnsubscribe)
    ).subscribe({
      complete: () => {
        this.loading = false;
      }
    });

    if (this.config.tableType === 'ungrouped') {
      this.createTableUngrouped(latestTagStates$);
    } else if (this.config.tableType === 'grouped') {
      this.createTableGrouped(latestTagStates$, this.config);
    }
  }

  public timestampAgeInMinutes(value: Parameters<typeof parseToDate>[0]) {
    const diff = differenceInMinutes(new Date(), parseToDate(value));
    if (diff === 0) {
      return '< 1';
    }
    return diff.toString();
  }

  private createTableUngrouped(latestTagStates$: Observable<TagpairState[]>) {
    const unsortedRows$ = latestTagStates$.pipe(
      map(tagpairStates => ({
        unsortedRows: tagpairStates.map(tagpairState => getTableRowUngrouped(tagpairState)),
        tagpairStates
      })
      )
    );
    const sortedRows$ = this.createSortedRows(unsortedRows$);
    this.tableData.ungrouped$ = createTableDataUngrouped(sortedRows$).pipe(
      takeUntil(this.ngUnsubscribe)
    );
  }

  private createTableGrouped(latestTagStates$: Observable<TagpairState[]>, config: ConfigGrouped) {
    const columnNames = config.columns.map(col => col.name);

    const unsortedRows$ = latestTagStates$.pipe(
      map(tagpairStates => createGroupedUnsortedRows(tagpairStates, config.groupNames, config.columns))
    );
    const sortedRows$ = this.createSortedRows(unsortedRows$);
    this.tableData.grouped$ = createTableDataGrouped(sortedRows$, columnNames).pipe(
      takeUntil(this.ngUnsubscribe)
    );
  }

  private createSortedRows<T extends Object>(unsortedRows$: Observable<{
    unsortedRows: T[],
    tagpairStates: TagpairState[]
  }>) {
    return combineLatest([unsortedRows$, this.sortEvents$]).pipe(
      map((inputs) => {
        const compare = (v1: string | number, v2: string | number) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
        const { unsortedRows, tagpairStates } = inputs[0];
        const { column, direction } = inputs[1];

        const patchedRows = this.patchRowsForSorting(unsortedRows, column);
        const sortedRows = [...patchedRows].sort((a, b) => {
          const aColumn = lodash.get(a, column);
          const bColumn = lodash.get(b, column);
          const res = compare(aColumn, bColumn);
          return direction === 'asc' ? res : -res;
        });
        return {
          tagpairStates,
          sortedRows
        };
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Patches rows so that sorting doesn't stop if undefined comparison values are encountered
   * Does not affect the values shown because isEmpty determines if it's shown or not, despite value existing
   * @param rows
   * @param column
   */
  private patchRowsForSorting(rows: any[], column: any) {
    const clonedRows = rows.map(row => lodash.clone(row));
    for (let index = 0; index < clonedRows.length; index++) {
      const element = clonedRows[index];
      if (index > 0 && lodash.get(element, column) === undefined) {
        lodash.set(clonedRows[index], column, lodash.get(clonedRows[index - 1], column));
      }
    }
    return clonedRows;
  }

}
